import {
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Download,
  FileText,
  RefreshCw,
  Receipt,
  Users,
  TrendingUp,
  ListChecks,
} from 'lucide-react';
import { DataTable, NoOpsSelected, PageError, PageLoading } from '../../../../components';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '../../../../components/ui/tabs';
import { formatCurrencyBrlFromCents } from '../../../../utils';
import { useImileFechamento } from './hooks/use-imile-fechamento';

export function ImileFechamento() {
  const {
    currentOps,
    data,
    summaryStats,
    loading,
    isFetching,
    error,
    resumoColumns,
    operacoesColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    loadData,
    quinzenaOptions,
    selectedInicio,
    setSelectedInicio,
    selectedQuinzenaOption,
    isCurrentQuinzena,
  } = useImileFechamento();

  if (!currentOps) {
    return (
      <NoOpsSelected message="Selecione uma operação para visualizar o fechamento quinzenal iMile." />
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-[hsl(210,50%,10%)]">
            Fechamento Quinzenal iMile
          </h1>
          <p className="text-sm text-[hsl(215,16%,47%)]">
            Consulte as operações fechadas por quinzena para a ops{' '}
            <span className="font-medium text-[hsl(215,16%,30%)]">{currentOps}</span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadData()}
          disabled={isFetching}
          className="flex items-center gap-2 shrink-0 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Seletor de quinzena */}
      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-[hsl(210,50%,10%)]">
              <CalendarClock className="h-4 w-4 text-[hsl(215,16%,47%)]" />
              Quinzena:
            </div>
            <Select value={selectedInicio} onValueChange={setSelectedInicio}>
              <SelectTrigger className="w-full sm:w-96 border-[hsl(214,32%,91%)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quinzenaOptions.map((opt) => (
                  <SelectItem key={opt.inicio} value={opt.inicio}>
                    <span className={opt.isCurrentQuinzena ? 'font-semibold text-amber-700' : ''}>
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {data && (
              <Badge
                variant={data.quinzenaCompleta ? 'default' : 'destructive'}
                className={
                  data.quinzenaCompleta
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shrink-0'
                    : 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100 shrink-0'
                }
              >
                {data.quinzenaCompleta ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Quinzena fechada
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {isCurrentQuinzena ? 'Em andamento' : 'Quinzena incompleta'}
                  </span>
                )}
              </Badge>
            )}
          </div>

          {selectedQuinzenaOption && (
            <p className="mt-2 text-xs text-[hsl(215,16%,47%)]">
              De{' '}
              <span className="font-medium">{selectedQuinzenaOption.inicio}</span>
              {' '}até{' '}
              <span className="font-medium">{selectedQuinzenaOption.fim}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Banner quinzena atual */}
      {isCurrentQuinzena && (
        <Alert className="border-amber-400 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 font-semibold">
            Você está visualizando a quinzena atual — dados incompletos
          </AlertTitle>
          <AlertDescription className="text-amber-700">
            A quinzena ainda está em andamento. Os dados refletem apenas as operações realizadas até
            hoje. O arquivo exportado conterá{' '}
            <code className="font-mono bg-amber-100 px-1 rounded text-xs">QUINZENA_NAO_FECHADA</code>{' '}
            no nome.
          </AlertDescription>
        </Alert>
      )}

      <PageError error={error} />
      <PageLoading loading={loading} />

      {!loading && data && (
        <>
          {/* Aviso de quinzena passada incompleta */}
          {!isCurrentQuinzena && !data.quinzenaCompleta && (
            <Alert className="border-red-300 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-700 font-semibold">
                Quinzena com dias sem romaneio
              </AlertTitle>
              <AlertDescription className="text-red-600 space-y-2">
                <p>Os seguintes dias não possuem romaneio iMile carregado:</p>
                {data.diasFaltantes.length > 0 && (
                  <ul className="list-disc list-inside space-y-0.5 text-sm pl-1">
                    {data.diasFaltantes.map((d) => (
                      <li key={`${d.ops}-${d.date}`}>
                        <strong>{d.ops}</strong> — {d.date}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-sm font-medium">
                  Arquivos exportados conterão{' '}
                  <code className="font-mono bg-red-100 px-1 rounded text-xs">
                    QUINZENA_NAO_FECHADA
                  </code>{' '}
                  no nome.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Aviso de dias faltando na quinzena atual */}
          {isCurrentQuinzena && !data.quinzenaCompleta && data.diasFaltantes.length > 0 && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-700 font-semibold">
                Dias sem romaneio até agora
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                <ul className="list-disc list-inside space-y-0.5 text-sm mt-1 pl-1">
                  {data.diasFaltantes.map((d) => (
                    <li key={`${d.ops}-${d.date}`}>
                      <strong>{d.ops}</strong> — {d.date}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Cards de resumo */}
          {summaryStats && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Entregadores
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.totalEntregadores}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-violet-50">
                      <ListChecks className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Operações
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.totalOperacoes}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-50">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Valor Face Total
                      </p>
                      <p className="text-lg font-bold text-emerald-700 leading-tight">
                        {formatCurrencyBrlFromCents(summaryStats.valorFaceTotal)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <Receipt className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Com Comprovante
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.comComprovante}
                        <span className="text-sm font-normal text-[hsl(215,16%,47%)] ml-1">
                          / {summaryStats.totalOperacoes}
                        </span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Abas */}
          <Tabs defaultValue="resumo" className="w-full">
            <PageTabsList>
              <PageTabsTrigger value="resumo">
                <Users className="h-4 w-4" />
                Resumo por Entregador
                {data.resumo.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {data.resumo.length}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger value="operacoes">
                <FileText className="h-4 w-4" />
                Operações Detalhadas
                {data.operacoes.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {data.operacoes.length}
                  </Badge>
                )}
              </PageTabsTrigger>
            </PageTabsList>

            {/* Tab: Resumo */}
            <TabsContent value="resumo" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Resumo agregado por entregador
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Valor face somado de todas as operações da quinzena para cada entregador
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadResumo}
                    disabled={data.resumo.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.resumo.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">
                      Nenhum dado de resumo para este período.
                    </p>
                  ) : (
                    <DataTable
                      data={data.resumo}
                      columns={resumoColumns}
                      searchPlaceholder="Buscar por documento ou nome..."
                      pageSize={50}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Operações */}
            <TabsContent value="operacoes" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Operações individuais
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Cada linha representa uma antecipação realizada na quinzena
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadOperacoes}
                    disabled={data.operacoes.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.operacoes.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">
                      Nenhuma operação encontrada para este período.
                    </p>
                  ) : (
                    <DataTable
                      data={data.operacoes}
                      columns={operacoesColumns}
                      searchPlaceholder="Buscar por documento ou nome..."
                      pageSize={100}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
